import { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { Lock } from 'lucide-react';
import { Button, Card } from '@/shared/ui';
import { AgentChip } from '../AgentChip';
import type { AgentIdentity } from '../../utils/agentLabels';
import {
  DEFAULT_ENGAGEMENT_MODE,
  ENGAGEMENT_OPTIONS,
  diffPreference,
  formFromPreference,
  type CommsFormState,
  type CommunicationPreference,
  type CommunicationPreferencePatch,
} from '../../utils/commsLabels';
import styles from './CommsPreferenceCard.module.css';

function Switch({
  checked,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className={styles.switch}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-label={label}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className={styles.slider} />
    </label>
  );
}

/**
 * One agent's communication preferences — the same card on both front doors (Command Center and the
 * practice's Settings → Notifications), because they edit the same record. One toggle ("Email the
 * owner") for every agent; engagement mode only for an agent whose skills emit checkpoints. The
 * always-ask floor is shown, never editable.
 */
export function CommsPreferenceCard({
  preference,
  saving,
  canEdit = true,
  door = 'practice',
  onSave,
}: {
  preference: CommunicationPreference;
  saving: boolean;
  canEdit?: boolean;
  /** 'agency' (Command Center) may lock email; 'practice' (Settings → Notifications) sees the lock. */
  door?: 'agency' | 'practice';
  onSave: (patch: CommunicationPreferencePatch) => void;
}) {
  const [form, setForm] = useState<CommsFormState>(() => formFromPreference(preference));

  // A save elsewhere (the other front door) lands here on refetch; re-seed the form from it.
  useEffect(() => {
    setForm(formFromPreference(preference));
  }, [preference]);

  const patch = useMemo(() => diffPreference(preference, form), [preference, form]);
  const dirty = Object.keys(patch).length > 0;
  const agents = useMemo<AgentIdentity[]>(
    () => [
      {
        roleKey: preference.agentType,
        personaName: preference.personaName,
        displayName: preference.displayName,
      },
    ],
    [preference.agentType, preference.personaName, preference.displayName],
  );
  const agentName = preference.personaName;
  const effectiveMode = form.engagementMode ?? DEFAULT_ENGAGEMENT_MODE;

  const disabled = !canEdit || saving;
  const emailLockedForPractice = door === 'practice' && preference.emailLockedByAgency;

  return (
    <Card>
      <div className={styles.head}>
        <AgentChip agentType={preference.agentType} agents={agents} />
        <div className={styles.headText}>
          <div className={styles.headTitle}>{agentName}</div>
          <div className={styles.headSub}>
            {preference.displayName}
            {preference.isDefault
              ? ' · using defaults'
              : preference.updatedAt
                ? ` · saved ${new Date(preference.updatedAt).toLocaleDateString()}`
                : ''}
          </div>
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.rowText}>
          <div className={styles.rowLabel}>Email the owner</div>
          <div className={styles.rowHint}>
            {emailLockedForPractice
              ? 'Managed by your PracticeNova team for now. Everything still shows in your dashboard.'
              : `Sent from PracticeNova on ${agentName}’s behalf, alongside the dashboard tasks inbox`}
          </div>
        </div>
        <div className={styles.control}>
          <Switch
            checked={form.emailEnabled}
            disabled={disabled || emailLockedForPractice}
            label={`Email the owner for ${agentName}`}
            onChange={(on) => setForm((f) => ({ ...f, emailEnabled: on }))}
          />
        </div>
      </div>

      {door === 'agency' && (
        <div className={styles.row}>
          <div className={styles.rowText}>
            <div className={styles.rowLabel}>Lock this setting</div>
            <div className={styles.rowHint}>
              The practice can’t change {agentName}’s email from its own settings. Use it to run{' '}
              {agentName} in the dashboard only.
            </div>
          </div>
          <div className={styles.control}>
            <Switch
              checked={form.emailLockedByAgency}
              disabled={disabled}
              label={`Lock email setting for ${agentName}`}
              onChange={(on) => setForm((f) => ({ ...f, emailLockedByAgency: on }))}
            />
          </div>
        </div>
      )}

      {preference.supportsEngagementMode && (
        <>
          <div className={styles.sectionLabel}>How much to involve you</div>
          <div
            className={styles.segmented}
            role="radiogroup"
            aria-label={`Engagement mode for ${agentName}`}
          >
            {ENGAGEMENT_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={effectiveMode === o.value}
                disabled={disabled}
                className={clsx(styles.segment, effectiveMode === o.value && styles.segmentActive)}
                onClick={() => setForm((f) => ({ ...f, engagementMode: o.value }))}
              >
                <span className={styles.segmentLabel}>{o.label}</span>
                <span className={styles.segmentHint}>{o.hint}</span>
              </button>
            ))}
          </div>
          {form.engagementMode === null && (
            <div className={styles.segmentDefault}>
              No choice made yet — {agentName} is on the default, Keep me informed.
            </div>
          )}

          <div className={styles.locked}>
            <div className={styles.lockedTitle}>
              <Lock size={14} aria-hidden="true" /> {agentName} always asks first
            </div>
            <ul className={styles.lockedList}>
              {preference.alwaysAsk.map((f) => (
                <li key={f.key}>{f.label}</li>
              ))}
            </ul>
          </div>
        </>
      )}

      <div className={styles.footer}>
        <span className={styles.footerNote}>
          {canEdit
            ? 'Changes here show in the Command Center and in the practice’s settings alike.'
            : 'Only a practice admin or owner can change these.'}
        </span>
        {canEdit && (
          <Button size="sm" disabled={!dirty || saving} onClick={() => onSave(patch)}>
            {saving ? 'Saving…' : `Save ${agentName}’s preferences`}
          </Button>
        )}
      </div>
    </Card>
  );
}
